import React from "react";

interface ValidateItem<T> {
  state: ValidateState;
  setState: React.Dispatch<React.SetStateAction<ValidateState>>;
  validate: (data: T) => boolean;
  clearError: () => void;
}

interface ValidateState {
  helperTextWhenError: string;
  error: boolean;
}

interface UseValidatorParams {
  commercialBookTypeCompanyName: boolean;
  commercialBookTypeCompanyNumber: boolean;
  searchArea: "city" | "country";
}

interface Validator {
  pref: ValidateItem<number | "">;
  city: ValidateItem<string>;
  commercialBookName: ValidateItem<string>;
  commercialBookNumberPart1: ValidateItem<string>;
  commercialBookNumberPart2: ValidateItem<string>;
  commercialBookNumberPart3: ValidateItem<string>;
}

const useValidator = (params: UseValidatorParams): Validator => {
  const [statePrefecture, setStatePrefecture] = React.useState<ValidateState>({
    error: false,
    helperTextWhenError: "",
  });
  const [stateCity, setStateCity] = React.useState<ValidateState>({
    error: false,
    helperTextWhenError: "",
  });
  const [stateCommercialBookName, setStateCommercialBookName] =
    React.useState<ValidateState>({ error: false, helperTextWhenError: "" });
  const [stateCommercialBookNumberPart1, setStateCommercialBookNumberPart1] =
    React.useState<ValidateState>({ error: false, helperTextWhenError: "" });
  const [stateCommercialBookNumberPart2, setStateCommercialBookNumberPart2] =
    React.useState<ValidateState>({ error: false, helperTextWhenError: "" });
  const [stateCommercialBookNumberPart3, setStateCommercialBookNumberPart3] =
    React.useState<ValidateState>({ error: false, helperTextWhenError: "" });

  // 都道府県のバリデーション
  const pref: ValidateItem<number | ""> = {
    state: statePrefecture,
    setState: setStatePrefecture,
    validate: (data: number | ""): boolean => {
      if (params.searchArea === "city" && data === "") {
        setStatePrefecture({
          error: true,
          helperTextWhenError: "都道府県を選択してください",
        });
        return false;
      }

      setStatePrefecture({ error: false, helperTextWhenError: "" });
      return true;
    },
    clearError: (): void => {
      setStatePrefecture({ error: false, helperTextWhenError: "" });
    },
  };

  // 市区町村のバリデーション
  const city: ValidateItem<string> = {
    state: stateCity,
    setState: setStateCity,
    validate: (data: string): boolean => {
      if (params.searchArea === "city" && data === "") {
        setStateCity({
          error: true,
          helperTextWhenError: "市区町村を選択してください",
        });
        return false;
      }

      setStateCity({ error: false, helperTextWhenError: "" });
      return true;
    },
    clearError: (): void => {
      setStateCity({ error: false, helperTextWhenError: "" });
    },
  };

  // 商号・名称のバリデーション
  const commercialBookName: ValidateItem<string> = {
    state: stateCommercialBookName,
    setState: setStateCommercialBookName,
    validate: (inputCommercialBookName: string): boolean => {
      if (inputCommercialBookName === "") {
        setStateCommercialBookName({
          error: true,
          helperTextWhenError:
            "商号・名称を入力してください（市区町村検索は1文字以上、全国検索は2文字以上）。",
        });
        return false;
      }

      if (inputCommercialBookName.length > 40) {
        setStateCommercialBookName({
          error: true,
          helperTextWhenError: "商号・名称は40文字以内で入力してください。",
        });
        return false;
      }

      if (
        params?.searchArea === "country" &&
        inputCommercialBookName.length <= 1
      ) {
        setStateCommercialBookName({
          error: true,
          helperTextWhenError:
            "全国検索では、1文字のみの前方一致検索はできません。2文字以上で検索し直すか、完全一致で商号・名称を全部入力して検索してください。",
        });
        return false;
      }

      setStateCommercialBookName({ error: false, helperTextWhenError: "" });
      return true;
    },
    clearError: (): void => {
      setStateCommercialBookName({ error: false, helperTextWhenError: "" });
    },
  };

  // 会社法人等番号のバリデーション
  const commercialBookNumberPart1: ValidateItem<string> = {
    state: stateCommercialBookNumberPart1,
    setState: setStateCommercialBookNumberPart1,
    validate: (data: string): boolean => {
      if (/^[0-9０-９]{4}$/g.test(data)) {
        setStateCommercialBookNumberPart1({
          error: false,
          helperTextWhenError: "",
        });
        return true;
      } else {
        setStateCommercialBookNumberPart1({
          error: true,
          helperTextWhenError: "会社法人等番号を入力してください",
        });
        return false;
      }
    },
    clearError: (): void => {
      setStateCommercialBookNumberPart1({
        error: false,
        helperTextWhenError: "",
      });
    },
  };

  const commercialBookNumberPart2: ValidateItem<string> = {
    state: stateCommercialBookNumberPart2,
    setState: setStateCommercialBookNumberPart2,
    validate: (data: string): boolean => {
      if (/^(01|02|03|04|05|０１|０２|０３|０４|０５)$/g.test(data)) {
        setStateCommercialBookNumberPart2({
          error: false,
          helperTextWhenError: "",
        });
        return true;
      } else {
        setStateCommercialBookNumberPart2({
          error: true,
          helperTextWhenError: "会社法人等番号（01〜05）を入力してください",
        });
        return false;
      }
    },
    clearError: (): void => {
      setStateCommercialBookNumberPart2({
        error: false,
        helperTextWhenError: "",
      });
    },
  };
  const commercialBookNumberPart3: ValidateItem<string> = {
    state: stateCommercialBookNumberPart3,
    setState: setStateCommercialBookNumberPart3,
    validate: (data: string): boolean => {
      if (/^[0-9０-９]{6}$/g.test(data)) {
        setStateCommercialBookNumberPart3({
          error: false,
          helperTextWhenError: "",
        });
        return true;
      } else {
        setStateCommercialBookNumberPart3({
          error: true,
          helperTextWhenError: "会社法人等番号を入力してください",
        });
        return false;
      }
    },
    clearError: (): void => {
      setStateCommercialBookNumberPart3({
        error: false,
        helperTextWhenError: "",
      });
    },
  };

  return {
    pref,
    city,
    commercialBookName,
    commercialBookNumberPart1,
    commercialBookNumberPart2,
    commercialBookNumberPart3,
  };
};

export { useValidator };
